import {
  HashtagApiResponse,
  HashtagSourcesApiResponse,
  StoryApiResponse,
} from 'api/api.types';
import {
  ArrowLeftSvg,
  Box,
  CaretRightSvg,
  Flex,
  Heading,
  Link,
  PrimaryButton,
  Text,
  color,
  spacing,
  typography,
  useColorMode,
  useDeviceDetect,
  useIsMobileView,
} from 'deepstash-ui';
import { useRouter } from 'next/router';
import React, { ReactElement, useEffect, useMemo } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { Analytics, Events } from 'services/analytics';
import Lottie from 'src/components/animation/Lottie';
import GetTheAppBanner from 'src/components/banner/GetTheAppBanner';
import PreviewCardSmall from 'src/components/cards/PreviewCardSmall';
import IdeaCard from 'src/components/idea-card/IdeaCard';
import NextImage from 'src/components/image/NextImage';
import Layout from 'src/components/layout/Layout';
import { LayoutSection } from 'src/components/layout/Layout.types';
import TopicMetacontent from 'src/components/metacontent/TopicMetacontent';
import SubsectionHeader from 'src/components/metacontent/components/SubsectionHeader';
import NextLink from 'src/components/navigation/NextLink';
import SourceCollapsed from 'src/components/source/source-collapsed/SourceCollapsed';
import StoryCard from 'src/components/story/StoryCard';
import LANDING_ANIMATION_1 from 'theme/animations/landing_1.json';
import LANDING_ANIMATION_2 from 'theme/animations/landing_2.json';
import LANDING_ANIMATION_3 from 'theme/animations/landing_3.json';
import { MetaTopic } from 'types/models';
import {
  GAP_SIZE_REM,
  SECTION_GAP_REM,
  formatStringToHashtagCase,
  getRemWidthFromColumns,
} from 'utils/global';
import { getTopicPageMetadata } from 'utils/metadata.utils';
import { normalizeIdea, normalizeSource } from 'utils/normalizers/source';
import StoreRatingCard from '../landing/StoreRatingCard';
import LandingIllustration1 from '../landing/illustrations/LandingIllustration1';
import LandingIllustration2 from '../landing/illustrations/LandingIllustration2';
import LandingIllustration3 from '../landing/illustrations/LandingIllustration3';
import CollectionInHeader from '../source/components/CollectionInHeader';
import TopicContentNSI from './components/TopicContentNSI';
import { UNSORTED_TOPICS } from 'utils/metatopics.constants';

const PHONE_IMAGE_URL = 'https://static.deepstash.com/phone-landing-image.png';
const CURATORS_IMAGE_URL =
  'https://static.deepstash.com/images/web/misc/social-ad.webp';

const RELATED_TOPICS: MetaTopic[] = [
  {
    id: 1,
    exploreId: 'hashtag-productivity',
    name: 'Productivity',
    image:
      'https://static.deepstash.com/illustrations/topics/svg/202305/productivity.svg',
    categoryOrder: 0,
  },
  {
    id: 6,
    exploreId: 'hashtag-psychology',
    name: 'Psychology',
    image:
      'https://static.deepstash.com/illustrations/topics/svg/202305/psychology.svg',
    categoryOrder: 0,
  },
  {
    id: 18,
    exploreId: 'hashtag-education',
    name: 'Education',
    image:
      'https://static.deepstash.com/illustrations/topics/svg/202305/education.svg',
    categoryOrder: 0,
  },
  {
    id: 4,
    exploreId: 'hashtag-loveandrelationships',
    name: 'Love and Relationships',
    image:
      'https://static.deepstash.com/illustrations/topics/svg/202305/loveandrelationships.svg',
    categoryOrder: 0,
  },
  {
    id: 13,
    exploreId: 'hashtag-leadershipandmanagement',
    name: 'Leadership and Management',
    image:
      'https://static.deepstash.com/illustrations/topics/svg/202305/leadership.svg',
    categoryOrder: 0,
  },
];

interface TopicPageProps {
  topicTag: string;
  hashtagApi: HashtagApiResponse;
  hashtagSources: HashtagSourcesApiResponse;
  stories: StoryApiResponse[];
}

const TopicPageNSI: React.FC<TopicPageProps> = ({
  hashtagApi,
  hashtagSources,
  stories,
}) => {
  const { colorMode, setColorMode } = useColorMode();
  const isMobileView = useIsMobileView();
  const { isIos } = useDeviceDetect();
  const router = useRouter();

  useEffect(() => {
    setColorMode('light');
  }, []);

  const ideaCarousel = useMemo(() => {
    if (!hashtagApi.seo) return null;
    const { headings } = hashtagApi.seo;
    return (hashtagApi.seo?.blocks?.length ?? 0) > 0 ? (
      <Flex flexDirection="column" w="100%" justifyContent="center">
        <Heading mb={spacing.M.rem} size="h2">
          {ReactHtmlParser(headings[2])}
        </Heading>
        <Flex
          gridGap={GAP_SIZE_REM}
          justifyContent="left"
          width="100%"
          maxW={{ base: 'calc(100vw - 64px)', md: '100%' }}
          overflowX="scroll"
          alignItems="flex-start"
        >
          {hashtagApi.seo?.blocks?.map((ideaApiResponse, index) => {
            const normalizedArticle = ideaApiResponse.article
              ? normalizeSource(ideaApiResponse.article)
              : null;
            return (
              <Flex
                minW={{ base: '100%', md: getRemWidthFromColumns(4) }}
                flexDirection="column"
                key={`idea-${index}`}
              >
                {normalizedArticle && (
                  <>
                    <SubsectionHeader mb={0}>
                      Core idea curated from:
                    </SubsectionHeader>
                    <SourceCollapsed
                      showContext={false}
                      source={normalizedArticle}
                      mb={spacing.M.rem}
                    />
                  </>
                )}
                <IdeaCard
                  minW={{ base: '100%', md: getRemWidthFromColumns(4) }}
                  ideaData={{
                    idea: normalizeIdea(ideaApiResponse),
                    source: normalizedArticle,
                  }}
                />
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    ) : null;
  }, [hashtagApi.seo?.blocks, hashtagApi.seo]);

  if (!hashtagApi.seo) {
    return null;
  }

  const { headings, descriptions, img_urls, collections } = hashtagApi.seo;

  const numberOfPages = Math.ceil(hashtagSources.meta.total_count / 5.0);
  const hashtagUrlParam = router.query.hashTag as string;

  const renderPages = () => {
    const arr: number[] = [];

    const PAGES_MAX_NAVIGATION = isMobileView ? 1 : 3;

    for (
      let i = Math.max(1, 1 - PAGES_MAX_NAVIGATION);
      i <= Math.min(1 + PAGES_MAX_NAVIGATION, numberOfPages);
      i++
    ) {
      arr.push(i);
    }
    arr.push(-1);

    arr.push(numberOfPages);

    return (
      <Flex maxW="100%" alignSelf={'center'} my={GAP_SIZE_REM}>
        {arr.map((element, index) =>
          element === -1 ? (
            <Box px={spacing.M.rem} py={spacing.XS.rem} mr={spacing.XS.rem}>
              <Text color={color[colorMode].text}>...</Text>
            </Box>
          ) : (
            <NextLink
              key={`pageIndex${index}`}
              href={
                element === 1 ? '/topic/[hashTag]' : '/topic/[hashTag]/[page]'
              }
              as={
                element === 1
                  ? `/topic/${hashtagUrlParam}`
                  : `/topic/${hashtagUrlParam}/${element}`
              }
            >
              <Box
                borderRadius={spacing.L.rem}
                px={spacing.M.rem}
                py={spacing.XS.rem}
                mr={spacing.XS.rem}
                _hover={{ cursor: 'pointer' }}
                backgroundColor={
                  element === 1
                    ? color[colorMode].surface
                    : color[colorMode].top
                }
                _active={undefined}
                _focus={undefined}
              >
                <Link
                  _hover={undefined}
                  _focus={undefined}
                  href={
                    element === 1
                      ? `/topic/${hashtagUrlParam}`
                      : `/topic/${hashtagUrlParam}/${element}`
                  }
                  color={color[colorMode].text}
                  title={(element || '').toString()}
                >
                  {element}
                </Link>
              </Box>
            </NextLink>
          ),
        )}

        <NextLink
          href="/topic/[hashTag]/[page]"
          as={`/topic/${hashtagUrlParam}/2`}
        >
          <Link
            _hover={undefined}
            _focus={undefined}
            href={`/topic/${hashtagUrlParam}/2`}
            color={color[colorMode].text}
            title={'2'}
          >
            <Flex
              px={spacing.XS.rem}
              py={spacing.toRem(6)}
              mr={spacing.XS.rem}
              borderRadius={spacing.L.rem}
              align={'center'}
              justifyContent={'center'}
              color={color[colorMode].textSecondary}
            >
              <CaretRightSvg />
            </Flex>
          </Link>
        </NextLink>
      </Flex>
    );
  };

  const renderTitleTextAndImageSection = ({
    title,
    text,
    imageUrl,
    imagePosition = 'down',
    headingSize = 'h2',
    objectFit = 'contain',
  }: {
    title: ReactElement;
    text?: ReactElement;
    imageUrl: string;
    imagePosition?: 'up' | 'down';
    headingSize?: 'h1' | 'h2';
    objectFit?: 'cover' | 'contain';
  }) => {
    const image = (
      <NextImage
        imageUrl={imageUrl}
        wrapperStyle={{
          width: isMobileView ? '100%' : getRemWidthFromColumns(4),
          height: isMobileView ? '60vw' : getRemWidthFromColumns(3),
          marginInline: 'auto',
          borderRadius: spacing.M.rem,
        }}
        objectFit={objectFit}
      />
    );

    return (
      <Flex
        width={{
          base: '100%',
          md: getRemWidthFromColumns(3),
          lg: getRemWidthFromColumns(4),
        }}
        flexDirection="column"
        gridGap={GAP_SIZE_REM}
        color={color[colorMode].text}
      >
        {imagePosition === 'up' && image}
        <Heading size={headingSize}>{title}</Heading>
        {text && <Text color={color[colorMode].textSecondary}>{text}</Text>}
        {imagePosition === 'down' && image}
      </Flex>
    );
  };

  const initialTextSection = (
    <Flex gridGap={GAP_SIZE_REM} flexDirection={{ base: 'column', md: 'row' }}>
      {renderTitleTextAndImageSection({
        title: <>{ReactHtmlParser(headings[0])}</>,
        text: <>{ReactHtmlParser(descriptions[0])}</>,
        imageUrl: isMobileView ? img_urls[0] : PHONE_IMAGE_URL,
        objectFit: isMobileView ? 'cover' : 'contain',
        headingSize: 'h1',
      })}
      {renderTitleTextAndImageSection({
        title: <>{ReactHtmlParser(headings[1])}</>,
        text: <>{ReactHtmlParser(descriptions[1])}</>,
        imageUrl: isMobileView ? PHONE_IMAGE_URL : img_urls[0],
        objectFit: isMobileView ? 'contain' : 'cover',
        imagePosition: isMobileView ? 'down' : 'up',
      })}
    </Flex>
  );

  const tryTheApp = (
    <Flex
      gridGap={GAP_SIZE_REM}
      w="100%"
      flexDirection="column"
      alignItems="center"
    >
      <Heading size="h2">{ReactHtmlParser(headings[3])}</Heading>
      <PrimaryButton
        size="lg"
        {...typography.primaryHeadingFonts.small.bold}
        onClick={() => {
          router.push('/setup');
          Analytics.logEvent({
            eventName: Events.onboarding.tapStart,
            properties: {
              'open-from-element': 'topic-page-try-it-for-free-1',
            },
            platforms: ['amplitude'],
          });
        }}
      >
        {/* TODO No Trial Changes */}
        {/* Try it for Free */}
        Start Today
      </PrimaryButton>
      {isMobileView ? (
        <Flex>
          <StoreRatingCard
            store={isIos ? 'ios' : 'android'}
            flexGrow={1}
            flexShrink={0}
            width="auto"
            height="auto"
          />
        </Flex>
      ) : (
        <Flex
          gridGap={getRemWidthFromColumns(1)}
          flexDirection={{ sm: 'column', md: 'row', lg: 'row' }}
        >
          <StoreRatingCard
            store="ios"
            flexGrow={1}
            flexShrink={0}
            width="auto"
            height="auto"
          />
          <StoreRatingCard
            store="android"
            flexGrow={1}
            flexShrink={0}
            width="auto"
            height="auto"
          />
        </Flex>
      )}
    </Flex>
  );

  const renderJourneyValueProp = ({
    title,
    animationData,
    startingIllustration,
    direction,
  }: {
    title: ReactElement;
    animationData?: any;
    startingIllustration: React.ReactElement;
    direction: 'left' | 'right';
  }) => {
    return (
      <Flex
        justifyContent="space-between"
        gridGap={{ base: spacing.M.rem, md: GAP_SIZE_REM }}
        flexDirection={{
          sm: 'column',
          md: direction === 'left' ? 'row' : 'row-reverse',
        }}
        alignItems="center"
      >
        <Heading
          size="h2"
          {...typography.primaryHeadingFonts.medium.bold}
          color={color.light.text}
          width={{ base: '100%', md: '50%' }}
          textAlign={{ sm: 'center', md: 'unset' }}
        >
          {title}
        </Heading>
        <Flex
          position="relative"
          alignItems="center"
          borderRadius={16}
          overflow="hidden"
          width={{ base: '100%', md: '50%' }}
          height="auto"
          mx={{ sm: 48, md: 0 }}
        >
          {startingIllustration}
          {animationData && (
            <Lottie
              style={{
                position: 'relative',
                // width: '100%',
                // height: '100%',
                cursor: 'default',
                bgColor: 'blue',
              }}
              options={{
                loop: true,
                autoplay: false,
                animationData,
              }}
              isClickToPauseDisabled={true}
              speed={1}
            />
          )}
        </Flex>
      </Flex>
    );
  };

  const journeyValueProps = (
    <Flex flexDirection="column" gridGap={GAP_SIZE_REM}>
      {renderJourneyValueProp({
        title: <>{ReactHtmlParser(headings[4])}</>,
        animationData: LANDING_ANIMATION_3,
        startingIllustration: <LandingIllustration3 position="absolute" />,
        direction: 'right',
      })}
      {renderJourneyValueProp({
        title: <>{ReactHtmlParser(headings[5])}</>,
        animationData: LANDING_ANIMATION_2,
        startingIllustration: <LandingIllustration2 position="absolute" />,
        direction: 'left',
      })}
    </Flex>
  );

  const journeys = (
    <Flex flexDirection="column" gridGap={GAP_SIZE_REM}>
      {collections.map((journey, index) => (
        <CollectionInHeader
          key={`journey-${index}`}
          collection={journey}
          showStartJourneyButton
          topic={hashtagApi.title}
        />
      ))}
      <Flex
        borderRadius={spacing.toRem(16)}
        backgroundColor={'#237CAE'}
        color={color.light.textInverted}
        w="100%"
        height={spacing.toRem(160)}
        paddingX={spacing.M.rem}
        overflow="hidden"
        justifyContent="space-between"
        alignItems="flex-end"
        cursor="pointer"
        onClick={() => router.push('/get-pro')}
      >
        <Flex
          flexDirection="column"
          padding={spacing.S.rem}
          marginBottom={spacing.M.rem}
        >
          <Text {...typography.primaryHeadingFonts.medium.semiBold}>
            Start 100+ Journeys
          </Text>
          <Text {...typography.primaryTextFonts.large.regular}>
            Covering over 50 topics
          </Text>
          <Flex
            alignItems="center"
            marginTop={spacing.M.rem}
            gridGap={spacing.XS.rem}
            {...typography.primaryTextFonts.large.semiBold}
          >
            <ArrowLeftSvg transform="rotate(180deg)" />
            Get Deepstash Pro
          </Flex>
        </Flex>
        <NextImage
          imageUrl="https://static.deepstash.com/images/web/misc/unlimited-journeys-cover.png"
          wrapperStyle={{
            width: '204px',
            height: '150px',
          }}
        />
      </Flex>
    </Flex>
  );

  const communityValueProps = (
    <Flex flexDirection="column" gridGap={GAP_SIZE_REM}>
      {renderJourneyValueProp({
        title: <>{ReactHtmlParser(headings[6])}</>,
        startingIllustration: (
          <NextImage
            imageUrl={CURATORS_IMAGE_URL}
            wrapperStyle={{
              width: isMobileView ? '100%' : getRemWidthFromColumns(4),
              height: isMobileView ? '50vw' : getRemWidthFromColumns(2.5),
              marginInline: 'auto',
            }}
            objectFit="cover"
          />
        ),
        direction: 'right',
      })}
      {renderJourneyValueProp({
        title: <>{ReactHtmlParser(headings[7])}</>,
        animationData: LANDING_ANIMATION_1,
        startingIllustration: <LandingIllustration1 position="absolute" />,
        direction: 'left',
      })}
    </Flex>
  );

  const renderTopicCarousel = ({ topics }: { topics: MetaTopic[] }) => {
    return topics.length > 0 ? (
      <Flex
        w="100%"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
        gridGap={GAP_SIZE_REM}
        pb={{ base: spacing.M.rem, md: 0 }}
      >
        <Heading size="h2" textAlign="center">
          Looking to explore even more ideas?
          <br />
          Why not{' '}
          <span style={{ color: color.light.primary.default }}>
            check out related topics:
          </span>
        </Heading>
        <Flex
          gridGap={GAP_SIZE_REM}
          justifyContent="left"
          maxW={{ base: 'calc(100vw - 64px)', md: '100%' }}
          overflowX="scroll"
          alignItems="flex-start"
        >
          {topics.map((topic, index) => (
            <PreviewCardSmall
              key={topic.id}
              w="160px"
              h="160px"
              name={topic.name}
              imageUrl={topic.image}
              gridColumn={{
                base: (index + 1) % 2,
                md: (index + 1) % 4,
              }}
              targetUrl={'/topic/' + formatStringToHashtagCase(topic.name)}
              hasShadow={false}
              p={spacing.M.rem}
            />
          ))}
        </Flex>
      </Flex>
    ) : null;
  };

  const renderStoryCarousel = ({
    stories,
  }: {
    stories: StoryApiResponse[];
  }) => {
    return stories.length > 0 ? (
      <Flex
        w="100%"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
        gridGap={GAP_SIZE_REM}
        pb={{ base: spacing.M.rem, md: 0 }}
        mb={{ base: SECTION_GAP_REM, md: 0 }}
      >
        <Heading size="h2" textAlign="center">
          Check out our latest Stories & Blog Entries
        </Heading>
        <Flex
          gridGap={GAP_SIZE_REM}
          justifyContent="left"
          maxW={{ base: 'calc(100vw - 64px)', md: '100%' }}
          overflowX="scroll"
          alignItems="flex-start"
        >
          {stories.map((story, index) => (
            <StoryCard
              key={`story-${index}`}
              story={story}
              imageWidth={'100%'}
              imageHeight={160}
              minW={160}
            />
          ))}
        </Flex>
        <PrimaryButton onClick={() => router.push('/stories')}>
          Check out more Stories here
        </PrimaryButton>
      </Flex>
    ) : null;
  };

  const topicPageSection: LayoutSection = {
    content: (
      <Flex
        maxW="100%"
        w={{ base: `calc(100vw - 64px)`, md: '100%' }}
        overflowX="hidden"
        flexDirection="column"
        gridGap={SECTION_GAP_REM}
      >
        {initialTextSection}
        {ideaCarousel}
        {tryTheApp}
        {journeyValueProps}
        {journeys}
        {communityValueProps}
        {renderTopicCarousel({ topics: RELATED_TOPICS })}
        {renderStoryCarousel({ stories: stories })}
      </Flex>
    ),
    metaContent: <GetTheAppBanner isNsi />,
    metaContentTopOffset: 32,
    metaContentStickyOffset: 32,
  };

  const topicContentSection: LayoutSection = {
    content: (
      <Flex w="100%" flexDirection="column" gridGap={GAP_SIZE_REM}>
        <Heading size="h2" textAlign="center">
          Browse through all Ideas, Tips, Facts & Insights
        </Heading>
        <TopicContentNSI
          sources={hashtagSources.objects.map(apiSource =>
            normalizeSource(apiSource),
          )}
        />
        {renderPages()}
      </Flex>
    ),
    metaContent: (
      <TopicMetacontent hashtagSourcesApiResponse={hashtagSources} />
    ),
    metaContentTopOffset: 96,
    metaContentStickyOffset: 32,
    sectionStyle: { marginTop: SECTION_GAP_REM },
  };

  return (
    <Layout
      pageMetadata={getTopicPageMetadata({
        topicTitle: hashtagApi.title,
        topicName: hashtagApi.name,
        pageTitle: hashtagApi.seo_title,
        pageDescription: hashtagApi.seo_description,
      })}
      sections={[topicPageSection, topicContentSection]}
      breadcrumbs={[
        {
          title: 'Home',
          link: '/',
        },
        {
          title: UNSORTED_TOPICS[hashtagApi.name]?.title ?? hashtagApi.name,
          link: `/topic/${hashtagApi.name}`,
        },
      ]}
      disableProAd
    />
  );
};

export default TopicPageNSI;
